import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  AccountTree as SopIcon,
  Close as CloseIcon,
  OpenInNew as DetailsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WarningAmber as RiskIcon,
  Nightlight as DormantIcon,
  BuildCircle as SetupIcon,
  RocketLaunch as DemoReadyIcon,
  TrendingUp as DeepIcon,
  CheckCircle as OperatingIcon,
  School as SchoolIcon,
  Save as SaveIcon,
  Today as DailyIcon,
  Login as LoginIcon,
  Payments as FeeIcon,
  FactCheck as AttendanceIcon,
  Campaign as NoticeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const BANDS = [
  "ALL",
  "At risk",
  "Dormant",
  "Setup gap",
  "Demo ready",
  "Operating",
  "Deep use",
];

const BAND_COLOR = {
  "At risk": crmColors.error.main,
  Dormant: crmColors.secondary.main,
  "Setup gap": crmColors.warning.main,
  "Demo ready": crmColors.info?.main || "#0288D1",
  Operating: crmColors.primary.main,
  "Deep use": crmColors.success.main,
  Unknown: crmColors.secondary.light,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

//Created By Cursor
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

//Created By Cursor
const SummaryTile = ({ label, value, color, icon, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 1.6,
      height: "100%",
      borderRadius: "14px",
      cursor: "pointer",
      border: `1px solid ${alpha(color, active ? 0.5 : 0.22)}`,
      background: `linear-gradient(145deg, ${alpha(color, active ? 0.16 : 0.08)} 0%, #fff 72%)`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      transition: "transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        borderColor: alpha(color, 0.45),
        boxShadow: `0 8px 18px ${alpha(color, 0.12)}`,
      },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "11px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(color, 0.14),
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: crmColors.text.secondary,
          lineHeight: 1.15,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 20, color: crmColors.text.primary, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

//Created By Cursor
const ScoreBar = ({ label, value, max = 40, color }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: crmColors.text.secondary }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800 }}>{value}/{max}</Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, (Number(value) / max) * 100)}
      sx={{
        height: 6,
        borderRadius: 99,
        bgcolor: alpha(color, 0.12),
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 99 },
      }}
    />
  </Box>
);

//Created By Cursor
const DialogSection = ({ title, hint, children }) => (
  <Box
    sx={{
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: "14px",
      border: `1px solid ${crmColors.border.default}`,
      background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.03)} 0%, ${crmColors.background.paper} 100%)`,
    }}
  >
    <Box sx={{ mb: 1.15 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: crmColors.text.primary,
        }}
      >
        {title}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
    {children}
  </Box>
);

const SOP = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("health"); // health | daily
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [band, setBand] = useState("ALL");
  const [stage, setStage] = useState("ALL");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [dailySummary, setDailySummary] = useState(null);
  const [dailyDate, setDailyDate] = useState("");
  const [dailyLoading, setDailyLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editStage, setEditStage] = useState("1");
  const [editTraining, setEditTraining] = useState("");
  const [editWorking, setEditWorking] = useState("");
  const [editNext, setEditNext] = useState("");
  const [saving, setSaving] = useState(false);
  const firstLoadRef = useRef(true);

  const stageOptions = useMemo(() => {
    const labels = summary?.stage_labels || [];
    if (labels.length) return labels;
    return [
      { stage: "1", label: "Account live" },
      { stage: "2", label: "Academic setup" },
      { stage: "3", label: "Fee setup" },
      { stage: "4", label: "First operating week" },
      { stage: "5", label: "Parent habit" },
      { stage: "6", label: "Exam cycle" },
      { stage: "7", label: "Save and expand" },
    ];
  }, [summary]);

  //Created By Cursor — auto-refresh only on first open; filters read cached health
  const loadList = useCallback(
    async ({ autoRefresh } = {}) => {
      setLoading(true);
      try {
        const ar =
          autoRefresh != null ? autoRefresh : firstLoadRef.current ? 1 : 0;
        const res = await apiService.getSopSchoolList({
          health_band: band,
          sop_stage: stage,
          q: query,
          limit: 300,
          offset: 0,
          auto_refresh: ar,
        });
        firstLoadRef.current = false;
        if (res.success) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setTotal(res.total || 0);
          setSummary(res.summary || null);
        } else {
          toast.error(res.error || res.message || "Failed to load SOP list");
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load SOP list");
      } finally {
        setLoading(false);
      }
    },
    [band, stage, query]
  );

  //Created By Cursor — today's login / fee / attendance pulse
  const loadDaily = useCallback(async () => {
    setDailyLoading(true);
    try {
      const res = await apiService.getSopDailyActivity({
        q: query,
        limit: 300,
      });
      if (res.success) {
        setDailyRows(Array.isArray(res.data) ? res.data : []);
        setDailyTotal(res.total || 0);
        setDailySummary(res.summary || null);
        setDailyDate(res.date || "");
      } else {
        toast.error(res.error || res.message || "Failed to load daily activity");
        setDailyRows([]);
        setDailyTotal(0);
        setDailySummary(null);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load daily activity");
    } finally {
      setDailyLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (viewMode === "health") {
      loadList();
    }
  }, [loadList, viewMode]);

  useEffect(() => {
    if (viewMode === "daily") {
      loadDaily();
    }
  }, [loadDaily, viewMode]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  //Created By Cursor
  const onRefreshBatch = async () => {
    if (viewMode === "daily") {
      await loadDaily();
      toast.success("Daily activity refreshed");
      return;
    }
    setRefreshing(true);
    try {
      const res = await apiService.refreshSchoolSopHealth({ limit: 40, stale_hours: 1 });
      if (res.success) {
        toast.success(res.message || `Refreshed ${res.data?.refreshed || 0} schools`);
        await loadList({ autoRefresh: 0 });
      } else {
        toast.error(res.error || "Refresh failed");
      }
    } finally {
      setRefreshing(false);
    }
  };

  //Created By Cursor
  const openDetail = async (row) => {
    const code = row?.school_code;
    if (!code) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await apiService.getSopSchoolDetail(code);
      if (res.success && res.data) {
        setDetail(res.data);
        const h = res.data.health || {};
        setEditStage(String(h.sop_stage || "1"));
        setEditTraining(h.training_status || h.master_training_status || "");
        setEditWorking(h.working_status || h.master_working_status || "");
        setEditNext(h.next_action || "");
      } else {
        toast.error(res.error || "Could not load school SOP detail");
        setDetailOpen(false);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  //Created By Cursor
  const saveDetail = async () => {
    const code = detail?.health?.school_code;
    if (!code) return;
    setSaving(true);
    try {
      const res = await apiService.updateSopSchool({
        school_code: code,
        sop_stage: editStage,
        training_status: editTraining,
        working_status: editWorking,
        next_action: editNext,
      });
      if (res.success) {
        toast.success("SOP updated");
        setDetail(res.data);
        await loadList({ autoRefresh: 0 });
      } else {
        toast.error(res.error || "Update failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const bands = summary?.bands || {};
  const tracks = detail?.tracks || [];
  const health = detail?.health || null;
  const healthColor = BAND_COLOR[health?.health_band] || BAND_COLOR.Unknown;
  const ds = dailySummary || {};
  const pageLoading = viewMode === "daily" ? dailyLoading : loading;
  const pageTotal = viewMode === "daily" ? dailyTotal : total;

  const formatTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const actionBtnSx = {
    textTransform: "none",
    borderRadius: "10px",
    fontWeight: 800,
    px: 1.75,
    background: crmColors.energy.main,
    backgroundImage: "none",
    color: "#fff",
    border: `1px solid ${alpha("#fff", 0.55)}`,
    boxShadow: `0 4px 12px ${alpha(crmColors.energy.main, 0.4)}`,
    "&:hover": {
      background: crmColors.energy.dark,
      backgroundImage: "none",
    },
    "&.Mui-disabled": {
      bgcolor: alpha(crmColors.energy.main, 0.4),
      color: "#fff",
    },
  };

  return (
    <PageContentLayout
      title="Standard Operating Procedure"
      subtitle={
        viewMode === "daily"
          ? `SOP · Daily Activity${dailyDate ? ` · ${dailyDate}` : ""}`
          : "SOP · school setup completeness, daily usability & service stage"
      }
      barSubtitle={
        viewMode === "daily"
          ? `${pageTotal} active today`
          : `${pageTotal} school${pageTotal === 1 ? "" : "s"}`
      }
      breadcrumbs={[
        {
          label: "Standard Operating Procedure",
          icon: <SopIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/dashboard"
      darkBar
      actions={
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={
            refreshing || dailyLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 18 }} />
            )
          }
          disabled={refreshing || pageLoading}
          onClick={onRefreshBatch}
          sx={actionBtnSx}
        >
          {viewMode === "daily" ? "Refresh activity" : "Refresh scores"}
        </Button>
      }
    >
      <Stack spacing={1.75}>
        <Box
          sx={{
            borderRadius: "12px",
            border: `1px solid ${crmColors.border.default}`,
            bgcolor: "#fff",
            px: 1.25,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: crmColors.text.secondary,
            }}
          >
            View
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_e, next) => {
              if (!next) return;
              setViewMode(next);
            }}
            sx={{
              bgcolor: alpha(crmColors.primary.main, 0.04),
              borderRadius: "9px",
              p: 0.25,
              "& .MuiToggleButtonGroup-grouped": {
                border: 0,
                borderRadius: "7px !important",
                px: 1.35,
                py: 0.4,
                textTransform: "none",
                fontWeight: 800,
                fontSize: 12.5,
                color: crmColors.text.secondary,
                gap: 0.45,
                "&.Mui-selected": {
                  bgcolor: crmColors.primary.main,
                  color: "#fff",
                  boxShadow: `0 2px 8px ${alpha(crmColors.primary.main, 0.28)}`,
                  "&:hover": { bgcolor: crmColors.primary.dark },
                },
              },
            }}
          >
            <ToggleButton value="health">
              <SopIcon sx={{ fontSize: 15 }} />
              School Health
            </ToggleButton>
            <ToggleButton value="daily">
              <DailyIcon sx={{ fontSize: 15 }} />
              Daily Activity
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === "health" ? (
          <Grid container spacing={1.25}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="At risk"
                value={bands["At risk"] || 0}
                color={BAND_COLOR["At risk"]}
                icon={<RiskIcon sx={{ fontSize: 20 }} />}
                active={band === "At risk"}
                onClick={() => setBand(band === "At risk" ? "ALL" : "At risk")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Dormant"
                value={bands.Dormant || 0}
                color={BAND_COLOR.Dormant}
                icon={<DormantIcon sx={{ fontSize: 20 }} />}
                active={band === "Dormant"}
                onClick={() => setBand(band === "Dormant" ? "ALL" : "Dormant")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Setup gap"
                value={bands["Setup gap"] || 0}
                color={BAND_COLOR["Setup gap"]}
                icon={<SetupIcon sx={{ fontSize: 20 }} />}
                active={band === "Setup gap"}
                onClick={() => setBand(band === "Setup gap" ? "ALL" : "Setup gap")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Demo ready"
                value={bands["Demo ready"] || 0}
                color={BAND_COLOR["Demo ready"]}
                icon={<DemoReadyIcon sx={{ fontSize: 20 }} />}
                active={band === "Demo ready"}
                onClick={() => setBand(band === "Demo ready" ? "ALL" : "Demo ready")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Operating"
                value={bands.Operating || 0}
                color={BAND_COLOR.Operating}
                icon={<OperatingIcon sx={{ fontSize: 20 }} />}
                active={band === "Operating"}
                onClick={() => setBand(band === "Operating" ? "ALL" : "Operating")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Deep use"
                value={bands["Deep use"] || 0}
                color={BAND_COLOR["Deep use"]}
                icon={<DeepIcon sx={{ fontSize: 20 }} />}
                active={band === "Deep use"}
                onClick={() => setBand(band === "Deep use" ? "ALL" : "Deep use")}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1.25}>
            <Grid item xs={6} sm={3}>
              <SummaryTile
                label="Schools active"
                value={ds.schools_active || dailyTotal || 0}
                color={crmColors.primary.main}
                icon={<SchoolIcon sx={{ fontSize: 20 }} />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryTile
                label="Staff logins"
                value={ds.staff_logins || 0}
                color={crmColors.info.main}
                icon={<LoginIcon sx={{ fontSize: 20 }} />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryTile
                label="Fee receipts"
                value={ds.fee_receipts || 0}
                color={crmColors.success.main}
                icon={<FeeIcon sx={{ fontSize: 20 }} />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryTile
                label="Attendance sections"
                value={ds.attendance_sections || 0}
                color={crmColors.energy.main}
                icon={<AttendanceIcon sx={{ fontSize: 20 }} />}
              />
            </Grid>
          </Grid>
        )}

        <Box
          sx={{
            p: 1.5,
            borderRadius: "14px",
            border: `1px solid ${crmColors.border.default}`,
            background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.04)} 0%, ${crmColors.background.paper} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ md: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search school, code, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, minWidth: { md: 280 }, flex: 1 }}
            />
            {viewMode === "health" ? (
              <>
                <TextField
                  select
                  size="small"
                  label="Band"
                  value={band}
                  onChange={(e) => setBand(e.target.value)}
                  sx={{ ...fieldSx, minWidth: 140 }}
                >
                  {BANDS.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b === "ALL" ? "All bands" : b}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Stage"
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  sx={{ ...fieldSx, minWidth: 180 }}
                >
                  <MenuItem value="ALL">All stages</MenuItem>
                  {stageOptions.map((s) => (
                    <MenuItem key={s.stage} value={s.stage}>
                      {s.stage}. {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}
            <Chip
              size="small"
              label={
                viewMode === "daily"
                  ? `${pageTotal} with activity`
                  : `${pageTotal} schools`
              }
              sx={{
                height: 32,
                fontWeight: 800,
                bgcolor: alpha(crmColors.primary.main, 0.1),
                color: crmColors.primary.main,
              }}
            />
          </Stack>
        </Box>

        {viewMode === "daily" ? (
          pageLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
              <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
            </Box>
          ) : dailyRows.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 7,
                px: 2,
                borderRadius: "16px",
                border: `1px dashed ${crmColors.border.default}`,
                bgcolor: alpha(crmColors.primary.main, 0.02),
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>No daily activity yet</Typography>
              <Typography variant="caption" color="text.secondary">
                Schools with staff login, fee collection, or attendance today will appear here
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.15}>
              {dailyRows.map((row) => {
                const color = BAND_COLOR[row.health_band] || BAND_COLOR.Unknown;
                return (
                  <Box
                    key={row.school_code}
                    onClick={() => openDetail(row)}
                    sx={{
                      display: "flex",
                      borderRadius: "14px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: `1px solid ${crmColors.border.default}`,
                      background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                        crmColors.primary.main,
                        0.04
                      )} 100%)`,
                      "&:hover": {
                        borderColor: alpha(crmColors.primary.main, 0.32),
                        boxShadow: `0 8px 22px ${alpha(crmColors.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        alignSelf: "stretch",
                        background: color,
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.75,
                        py: 1.35,
                      }}
                    >
                      <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
                          {row.school_name || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: crmColors.text.secondary }}>
                          {row.school_code}
                          {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                        </Typography>
                        <Typography sx={{ mt: 0.35, fontSize: 11.5, color: crmColors.text.disabled }}>
                          Last activity · {formatTime(row.last_activity_at)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          icon={<LoginIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.staff_logins || 0} logins`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_login
                              ? alpha(crmColors.info.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_login
                              ? crmColors.info.dark || crmColors.info.main
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<FeeIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.fee_receipts || 0} fees`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_fee
                              ? alpha(crmColors.success.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_fee
                              ? crmColors.success.dark
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<AttendanceIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.attendance_sections || 0} att.`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_attendance
                              ? alpha(crmColors.energy.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_attendance
                              ? crmColors.energy.dark
                              : crmColors.text.secondary,
                          }}
                        />
                        {(row.notices || 0) > 0 ? (
                          <Chip
                            size="small"
                            icon={<NoticeIcon sx={{ fontSize: "14px !important" }} />}
                            label={`${row.notices} notices`}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              bgcolor: alpha(crmColors.primary.main, 0.1),
                              color: crmColors.primary.main,
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )
        ) : pageLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
            <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.02),
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: "auto",
                mb: 1.25,
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`,
                color: "#fff",
                boxShadow: `0 8px 18px ${alpha(crmColors.primary.main, 0.28)}`,
              }}
            >
              <SopIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: crmColors.text.primary }}>
              No SOP scores yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Refresh scores to compute setup and usability from live school data
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefreshBatch}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 800,
                bgcolor: crmColors.energy.main,
                "&:hover": { bgcolor: crmColors.energy.dark },
              }}
            >
              Refresh scores
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.15}>
            {rows.map((row) => {
              const color = BAND_COLOR[row.health_band] || BAND_COLOR.Unknown;
              return (
                <Box
                  key={row.school_code}
                  onClick={() => openDetail(row)}
                  sx={{
                    display: "flex",
                    borderRadius: "14px",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: `1px solid ${crmColors.border.default}`,
                    background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                      color,
                      0.05
                    )} 100%)`,
                    transition: "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: alpha(crmColors.primary.main, 0.32),
                      boxShadow: `0 8px 22px ${alpha(crmColors.primary.main, 0.1)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 4, sm: 5 },
                      alignSelf: "stretch",
                      background: color,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: { xs: 1.25, md: 2 },
                      px: { xs: 1.35, sm: 1.75 },
                      py: 1.35,
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(color, 0.14),
                        color,
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 20 }} />
                    </Box>

                    <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: 14.5,
                            color: crmColors.text.primary,
                            lineHeight: 1.25,
                          }}
                        >
                          {row.school_name || "—"}
                        </Typography>
                        <Chip
                          size="small"
                          label={row.health_band || "Unknown"}
                          sx={{
                            height: 20,
                            fontWeight: 800,
                            fontSize: 10.5,
                            color: "#fff",
                            bgcolor: color,
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                      </Stack>
                      <Typography sx={{ mt: 0.3, fontSize: 12, color: crmColors.text.secondary }}>
                        {row.school_code}
                        {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 12.5, fontWeight: 700, color: crmColors.text.primary }}>
                        Stage {row.sop_stage}. {row.sop_stage_label}
                        <Typography
                          component="span"
                          sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 600, color: crmColors.text.secondary }}
                        >
                          {row.subscription_status || "—"}
                        </Typography>
                      </Typography>
                    </Box>

                    <Stack
                      spacing={0.65}
                      sx={{ flex: "1 1 180px", minWidth: 140, maxWidth: { md: 220 } }}
                    >
                      <ScoreBar
                        label="Setup"
                        value={row.setup_score}
                        max={40}
                        color={crmColors.primary.main}
                      />
                      <ScoreBar
                        label="Usage"
                        value={row.usage_score}
                        max={40}
                        color={crmColors.success.main}
                      />
                      <ScoreBar
                        label="Parent"
                        value={row.parent_score}
                        max={20}
                        color={crmColors.energy.main}
                      />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>
                        Total {row.usability_score}/100
                      </Typography>
                    </Stack>

                    <Box sx={{ flex: "1 1 160px", minWidth: 140 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: crmColors.text.secondary }}>
                        Last fee · {formatDate(row.last_fee_at)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: crmColors.text.secondary, mt: 0.35 }}>
                        Attendance · {formatDate(row.last_attendance_at)}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.65,
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: crmColors.text.primary,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {row.next_action || "No next action"}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/schools/detail/${encodeURIComponent(row.school_code)}`);
                      }}
                      sx={{
                        ml: { xs: 0, md: "auto" },
                        border: `1px solid ${crmColors.border.default}`,
                        borderRadius: "10px",
                        color: crmColors.primary.main,
                      }}
                    >
                      <DetailsIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={detailOpen}
        onClose={() => !saving && setDetailOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${crmColors.border.default}`,
            boxShadow: `0 24px 48px ${alpha(crmColors.primary.darker, 0.18)}`,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 1.75,
            background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 48%, ${crmColors.hero.mid} 100%)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              right: -40,
              top: -50,
              width: 160,
              height: 160,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.06),
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", position: "relative", zIndex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.22)}`,
              }}
            >
              <SopIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.2 }} noWrap>
                {health?.school_name || "School SOP"}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.88, mt: 0.2 }} noWrap>
                {health?.school_code || "Loading…"}
                {health?.admin_phone ? ` · ${health.admin_phone}` : ""}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => !saving && setDetailOpen(false)}
            disabled={saving}
            sx={{
              color: "#fff",
              bgcolor: alpha("#fff", 0.12),
              border: `1px solid ${alpha("#fff", 0.18)}`,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
              "&:hover": { bgcolor: alpha("#fff", 0.22) },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            px: { xs: 1.75, sm: 2.25 },
            py: 2,
            bgcolor: alpha(crmColors.primary.main, 0.02),
          }}
        >
          {detailLoading || !health ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
            </Box>
          ) : (
            <Stack spacing={1.75}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={health.health_band}
                  sx={{ fontWeight: 800, color: "#fff", bgcolor: healthColor }}
                />
                <Chip
                  size="small"
                  label={`Score ${health.usability_score}/100`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(crmColors.primary.main, 0.1),
                    color: crmColors.primary.main,
                  }}
                />
                <Chip
                  size="small"
                  label={health.subscription_status || "No subscription"}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(crmColors.secondary.main, 0.1),
                    color: crmColors.text.secondary,
                  }}
                />
              </Stack>

              <DialogSection title="Usability scores" hint="Setup, usage & parent engagement">
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <ScoreBar
                      label="Setup"
                      value={health.setup_score}
                      max={40}
                      color={crmColors.primary.main}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ScoreBar
                      label="Usage"
                      value={health.usage_score}
                      max={40}
                      color={crmColors.success.main}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ScoreBar
                      label="Parent"
                      value={health.parent_score}
                      max={20}
                      color={crmColors.energy.main}
                    />
                  </Grid>
                </Grid>
                {health.next_action ? (
                  <Typography sx={{ mt: 1.25, fontSize: 13, fontWeight: 700, color: crmColors.text.primary }}>
                    Next · {health.next_action}
                  </Typography>
                ) : null}
              </DialogSection>

              <DialogSection title="Track points" hint="Setup checklist & friction signals">
                <Stack spacing={0.85}>
                  {tracks.map((t) => {
                    const isFriction = t.track_group === "Friction";
                    const ok = isFriction ? !t.is_done : !!t.is_done;
                    const label = isFriction
                      ? t.is_done
                        ? "Issue"
                        : "Clear"
                      : t.is_done
                        ? "Done"
                        : "Missing";
                    return (
                      <Box
                        key={t.track_code}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          alignItems: "center",
                          px: 1.25,
                          py: 1,
                          borderRadius: "10px",
                          border: `1px solid ${crmColors.border.default}`,
                          bgcolor: "#fff",
                        }}
                      >
                        <Box sx={{ flex: "1 1 160px", minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                            {t.track_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.track_group}
                            {t.last_actor_id ? ` · ${t.last_actor_id}` : ""}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={label}
                          sx={{
                            height: 22,
                            fontWeight: 800,
                            fontSize: 11,
                            bgcolor: ok
                              ? alpha(crmColors.success.main, 0.12)
                              : alpha(crmColors.error.main, 0.12),
                            color: ok ? crmColors.success.dark : crmColors.error.main,
                          }}
                        />
                        <Typography
                          sx={{
                            flex: "1 1 180px",
                            fontSize: 12,
                            color: crmColors.text.secondary,
                          }}
                        >
                          {t.remark || t.healthy_rule || "—"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </DialogSection>

              <DialogSection title="Update stage" hint="Training, working status & next action">
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="SOP stage"
                      value={editStage}
                      onChange={(e) => setEditStage(e.target.value)}
                      sx={fieldSx}
                    >
                      {stageOptions.map((s) => (
                        <MenuItem key={s.stage} value={s.stage}>
                          {s.stage}. {s.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Training status"
                      value={editTraining}
                      onChange={(e) => setEditTraining(e.target.value)}
                      placeholder="e.g. Retrain scheduled"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Working status"
                      value={editWorking}
                      onChange={(e) => setEditWorking(e.target.value)}
                      placeholder="e.g. Fee live"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Next action"
                      value={editNext}
                      onChange={(e) => setEditNext(e.target.value)}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </DialogSection>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${crmColors.border.default}`,
            bgcolor: alpha(crmColors.primary.main, 0.03),
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DetailsIcon />}
            disabled={!health?.school_code}
            onClick={() =>
              health?.school_code &&
              navigate(`/schools/detail/${encodeURIComponent(health.school_code)}`)
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              borderColor: crmColors.border.hover,
            }}
          >
            School details
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setDetailOpen(false)}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                borderColor: crmColors.border.hover,
                color: crmColors.text.primary,
                bgcolor: "#fff",
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              disabled={saving || detailLoading || !health}
              onClick={saveDetail}
              startIcon={
                saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
              }
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "10px",
                px: 2.25,
                bgcolor: crmColors.energy.main,
                boxShadow: `0 6px 16px ${alpha(crmColors.energy.main, 0.35)}`,
                "&:hover": { bgcolor: crmColors.energy.dark },
              }}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default SOP;
